import React, { useState } from 'react';

import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import { getQueryValue } from '@shared/helpers';
import { folderIdQueryParam } from '@shared/model/query-params';
import { TBreadCrumb } from '@shared/ui/BreadCrumbs/model/index.type';

import cls from './BreadCrumbs.module.css';

interface BreadCrumbsProps {
    breadCrumbs: TBreadCrumb[];
    currentFolderId: number;
    onClickBreadCrumb: (breadCrumbId: TBreadCrumb['id']) => void;
    classes?: string;
    onDrop?: (
        e: React.DragEvent<HTMLLIElement>,
        breadCrumbId: TBreadCrumb['id'] | 'default'
    ) => void;
}

export const BreadCrumbs = ({
    breadCrumbs,
    currentFolderId,
    onClickBreadCrumb,
    classes,
    onDrop,
}: BreadCrumbsProps) => {
    const { t } = useTranslation();
    const [dragOverId, setDragOverId] = React.useState<number | null>(null);
    const [isFileOver, setIsFileOver] = useState(false);
    const currentFolderQueryValue = getQueryValue({ query: folderIdQueryParam });

    const handleDragEnter = (e: React.DragEvent<HTMLLIElement>, id: number | null) => {
        e.preventDefault();
        if (String(currentFolderQueryValue) === String(id)) return;

        const types = Array.from(e.dataTransfer.types);
        if (types.includes('text/internal-file')) {
            setDragOverId(id);
            setIsFileOver(true);
        }
    };

    const handleDragLeave = (e: React.DragEvent<HTMLLIElement>) => {
        e.preventDefault();
        const relatedTarget = e.relatedTarget as Node | null;
        if (!e.currentTarget.contains(relatedTarget)) {
            setDragOverId(null);
            setIsFileOver(false);
        }
    };

    const handleDrop = (e: React.DragEvent<HTMLLIElement>, breadCrumbId: TBreadCrumb['id']) => {
        e.preventDefault();
        setDragOverId(null);
        setIsFileOver(false);
        onDrop?.(e, breadCrumbId);
    };

    const handleDragOver = (e: React.DragEvent<HTMLLIElement>) => {
        e.preventDefault();
        if (String(currentFolderQueryValue) === String(currentFolderId)) return;

        setIsFileOver(true);
    };

    const isRootSelected = currentFolderId === null;
    const maxVisible = 4;
    const currentIndex = breadCrumbs.findIndex((bc) => bc.id === currentFolderId);
    const pathToCurrent = currentIndex !== -1 ? breadCrumbs.slice(0, currentIndex + 1) : [];
    const needEllipsis = pathToCurrent.length > maxVisible + 1;
    const crumbsToShow = needEllipsis ? pathToCurrent.slice(-maxVisible) : pathToCurrent.slice(0);

    return (
        <div className={cn(cls.BreadCrumbsWrapper, [classes])}>
            <ul className={cls.BreadCrumbsList}>
                <li
                    className={cn(
                        cls.BreadCrumb,
                        isFileOver && dragOverId === null && cls.onDragOver
                    )}
                    onClick={() => onClickBreadCrumb(null)}
                    onDragEnter={(e) => handleDragEnter(e, null)}
                    onDragLeave={handleDragLeave}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, 'default')}
                >
                    <span className={cn(cls.BreadCrumbText, isRootSelected && cls.current)}>
                        {t('Files.BreadCrumbs.allFiles')}
                    </span>
                </li>

                {needEllipsis && (
                    <>
                        <span className={cls.Divider}>/</span>
                        <span className={cls.BreadCrumb}>...</span>
                    </>
                )}

                {/* {secondLastCrumb && ( */}
                {/*    <> */}
                {/*        <span className={cls.Divider}>/</span> */}
                {/*        <li */}
                {/*            title={secondLastCrumb.name} */}
                {/*            className={cn( */}
                {/*                cls.BreadCrumb, */}
                {/*                currentFolderId === secondLastCrumb.id && cls.current, */}
                {/*                isFileOver && dragOverId === secondLastCrumb.id && cls.onDragOver */}
                {/*            )} */}
                {/*            onClick={() => onClickBreadCrumb(secondLastCrumb.id)} */}
                {/*            onDragEnter={(e) => handleDragEnter(e, secondLastCrumb.id)} */}
                {/*            onDragLeave={handleDragLeave} */}
                {/*            onDragOver={handleDragOver} */}
                {/*            onDrop={(e) => handleDrop(e, secondLastCrumb.id)} */}
                {/*        > */}
                {/*            {secondLastCrumb.name} */}
                {/*        </li> */}
                {/*    </> */}
                {/* )} */}

                {/* {lastCrumb && ( */}
                {/*    <> */}
                {/*        <span className={cls.Divider}>/</span> */}
                {/*        <li */}
                {/*            title={lastCrumb.name} */}
                {/*            className={cn( */}
                {/*                cls.BreadCrumb, */}
                {/*                currentFolderId === lastCrumb.id && cls.current, */}
                {/*                isFileOver && dragOverId === lastCrumb.id && cls.onDragOver */}
                {/*            )} */}
                {/*            onClick={() => onClickBreadCrumb(lastCrumb.id)} */}
                {/*            onDragEnter={(e) => handleDragEnter(e, lastCrumb.id)} */}
                {/*            onDragLeave={handleDragLeave} */}
                {/*            onDragOver={handleDragOver} */}
                {/*            onDrop={(e) => handleDrop(e, lastCrumb.id)} */}
                {/*        > */}
                {/*            {lastCrumb.name} */}
                {/*        </li> */}
                {/*    </> */}
                {/* )} */}
                {crumbsToShow.map((crumb) => (
                    <React.Fragment key={crumb.id}>
                        <span className={cls.Divider}>/</span>
                        <li
                            title={crumb.name}
                            className={cn(
                                cls.BreadCrumb,
                                isFileOver && dragOverId === crumb.id && cls.onDragOver
                            )}
                            onClick={() => onClickBreadCrumb(crumb.id)}
                            onDragEnter={(e) => handleDragEnter(e, crumb.id)}
                            onDragLeave={handleDragLeave}
                            onDragOver={handleDragOver}
                            onDrop={(e) => handleDrop(e, crumb.id)}
                        >
                            <span
                                className={cn(
                                    cls.BreadCrumbText,
                                    currentFolderId === crumb.id && cls.current
                                )}
                            >
                                {crumb.name}
                            </span>
                        </li>
                    </React.Fragment>
                ))}
            </ul>
        </div>
    );
};
